/**
 * Waits measured on the GAME clock for a `MockEnvironment`.
 *
 * A starved page is not a broken mechanic: a wall-clock budget fails a test on a loaded machine
 * whose game merely ran slowly. These waits spend a budget of GAME time on a process
 * (`gameTimeMs(target)`), and keep a generous wall guard only for a clock that stopped — its failure
 * says how little the clock ran, so a stalled page never reads as a mechanic that failed (and `holdFor`'s
 * says so when its clock ran but its ticks stopped finishing, so a system throwing every tick never reads as
 * one either).
 */
import { describeLastError, failureMessage, waitFor as wallWaitFor, type WaitForOptions } from './wait.js';
import type { EnvironmentQueries, ProcessResolver, ProcessTarget } from './environment-lookup.js';
import { subscribeProcessTick } from './environment-ticks.js';

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
/** Float slack on a hold's end: sixty 1/60 s ticks are 1000 ms of game, not a rounding error short of it. */
const HOLD_SLACK_MS = 1e-6;
const requireGameMs = (what: string, ms: number) => {
  if (!Number.isFinite(ms) || ms < 0) throw new Error(`[test-utils] ${what} must be a finite number of game milliseconds ≥ 0 (got ${ms})`);
};

/** `waitFor`'s options, plus a budget on the game clock. */
export interface EnvWaitForOptions extends WaitForOptions {
  /** Budget measured on `gameTimeMs(target)`; when absent this is the plain wall-clock `waitFor` (and `timeout` applies). */
  gameBudgetMs?: number;
  /** Whose game clock the budget is measured on — `gameTimeMs`'s target. */
  target?: ProcessTarget;
  /** Wall guard against a clock that stopped (default 300 000). Its failure names how little the clock moved. */
  wallGuardMs?: number;
}

/** The game-clock waits every `MockEnvironment` answers, mixed into it by `initializer`. */
export interface WaitQueries {
  /**
   * `waitFor` whose budget is GAME time on a process: a starved page is not a broken mechanic. Without
   * `gameBudgetMs` it is the plain wall-clock `waitFor`. The condition runs at least once per poll and once
   * more after the budget is spent; when its LAST evaluation threw, the failure ends with that error.
   */
  waitFor<T>(condition: () => T | Promise<T>, options?: EnvWaitForOptions): Promise<NonNullable<T>>;
  /**
   * Watch `gameMs` of game time for something that must NOT happen: `broken()` returns why it happened, or
   * `null`. It is judged when the hold starts and inside every tick of the process `gameTimeMs(target)` reads
   * (`after` the tick's systems ran), and the hold ends at the tick that brings it to `gameMs` — so the verdict
   * never depends on how often the wall clock wakes, and a tick a system's throw aborted does not end the
   * judging. `broken()` runs INSIDE the target's tick, so it must be read-only: pumping the target from it
   * would update that process from inside its own tick. The hold is exact to one tick of its target — on a
   * variable-step target the closing tick is as long as the page's load made it, so a change just after the
   * window can land in the state that tick is judged on. The wall clock (polled every `interval`) never judges:
   * it only ends a hold that could not be judged — its clock stopped, or its clock ran all of `gameMs` while its
   * ticks had stopped finishing an after phase (a system throwing every tick) — and the failure says which, and at
   * what game time the ticks stopped (or that none ever finished). It settles even when the target is gone by then
   * (an area destroyed, a client removed), named as it was when the hold started. A `broken()` that throws fails
   * the hold with that error.
   */
  holdFor(gameMs: number, options: { target?: ProcessTarget; broken: () => string | null; message?: string; wallGuardMs?: number; interval?: number }): Promise<void>;
  /**
   * Hold a synthetic press until the input side OBSERVED it (never on the wall clock), releasing in `finally` —
   * on success and on failure alike. Budgeted on the game clock (default 15 000 ms).
   */
  holdUntil(options: { press: () => void; release: () => void; observed: () => boolean; gameBudgetMs?: number; target?: ProcessTarget; wallGuardMs?: number; interval?: number; message?: WaitForOptions['message'] }): Promise<{ observed: boolean }>;
}

/**
 * Builds the game-clock waits for one environment over its resolver (`holdFor` watches the ticks of the process
 * `gameTimeMs(target)` reads) and its queries (`gameTimeMs`).
 */
export const createWaitQueries = (resolver: ProcessResolver, queries: EnvironmentQueries): WaitQueries => {
  /** The game-clock wait `waitFor` and `holdUntil` share — a local function, so neither depends on `this`. */
  const onGameClock = async <T>(condition: () => T | Promise<T>, options: EnvWaitForOptions & { gameBudgetMs: number }, caller: 'waitFor' | 'holdUntil'): Promise<NonNullable<T>> => {
    const { gameBudgetMs, target, wallGuardMs = 300_000, interval = 16 } = options;
    requireGameMs(`${caller}: gameBudgetMs`, gameBudgetMs);
    resolver.oneProcess(target, caller); // the budget is spent on ONE clock
    const from = queries.gameTimeMs(target);
    const wallDeadline = Date.now() + wallGuardMs;
    let lastError: unknown;
    for (;;) {
      lastError = undefined; // only the LAST evaluation's reason is reported
      try {
        const value = await condition();
        if (value) return value as NonNullable<T>;
      } catch (err) {
        lastError = err; // "not ready yet"; the last evaluation's reason is reported if the wait fails
      }
      const used = Math.round(queries.gameTimeMs(target) - from);
      if (used >= gameBudgetMs) {
        throw new Error(`[test-utils] ${failureMessage(options.message, 'condition')} — not after ${used}ms of game time${describeLastError(lastError)}`);
      }
      if (Date.now() >= wallDeadline) {
        throw new Error(`[test-utils] ${failureMessage(options.message, 'condition')} — the game clock ran only ${used}ms in ${wallGuardMs}ms of wall time${describeLastError(lastError)}`);
      }
      await sleep(interval);
    }
  };

  return {
    waitFor<T>(condition: () => T | Promise<T>, options: EnvWaitForOptions = {}): Promise<NonNullable<T>> {
      const { gameBudgetMs } = options;
      return gameBudgetMs === undefined ? wallWaitFor(condition, options) : onGameClock(condition, { ...options, gameBudgetMs }, 'waitFor');
    },
    holdFor(gameMs, { target, broken, message = 'the hold', wallGuardMs = 60_000, interval = 16 }) {
      return new Promise<void>((resolve, reject) => {
        requireGameMs('holdFor: gameMs', gameMs);
        // The process whose clock `gameTimeMs(target)` reads: the hold is judged on ITS ticks.
        const process = resolver.oneProcess(target, 'holdFor');
        // Named once, now, while it resolves: by the time the guard fires the target may be gone (its area destroyed,
        // its client removed), and nothing in the guard's timer may throw — a throw there escapes the hold unsettled.
        const targetName = resolver.describeTarget(target);
        let held = 0;
        /** `held` at the last tick that finished its after phase (and was judged there); `null` while none has. */
        let lastJudgedMs: number | null = null;
        let settled = false;
        let stopTicks: () => void = () => undefined;
        let guard: ReturnType<typeof setTimeout> | undefined;
        // Settles once: the subscription stops (its listener is not called again) and the guard is cleared.
        const finish = (error?: unknown) => {
          settled = true;
          stopTicks();
          clearTimeout(guard);
          if (error === undefined) resolve();
          else reject(error);
        };
        /** Judge the state now, `held` ms of game into the hold; the hold ends with the tick that brings it to `gameMs`. */
        const judge = () => {
          let why: string | null;
          try {
            why = broken();
          } catch (err) {
            finish(err); // a throw inside a tick would only be logged by the framework: it fails the hold instead
            return;
          }
          if (why) finish(new Error(`[test-utils] ${why} (${Math.round(held)}ms of game into ${message})`));
          else if (held >= gameMs - HOLD_SLACK_MS) finish();
        };
        judge(); // the state the hold starts on
        if (settled) return;
        stopTicks = subscribeProcessTick(process, (deltaMs, phase) => {
          if (phase === 'before') {
            held += deltaMs;
          } else {
            lastJudgedMs = held;
            judge(); // what this tick produced
          }
        });
        // The wall clock never judges: it only ends a hold that could not be judged — its clock stopped, or its
        // clock ran all of the window while its ticks had stopped finishing an after phase (a system throwing every
        // tick). It reads only what the hold recorded, so nothing in it can throw.
        const wallDeadline = Date.now() + wallGuardMs;
        const watch = () => {
          if (Date.now() < wallDeadline) {
            guard = setTimeout(watch, interval);
          } else if (held >= gameMs - HOLD_SLACK_MS) {
            const unfinished = lastJudgedMs === null
              ? `none of its ticks finished its after phase in ${wallGuardMs}ms of wall time`
              : `its ticks stopped finishing their after phase at ${Math.round(lastJudgedMs)}ms of game`;
            finish(new Error(
              `[test-utils] ${message} cannot be judged: ${targetName} ran ${Math.round(held)}ms of game, all of the ${gameMs}ms hold, ` +
                `but ${unfinished} — a system may be throwing every tick.`,
            ));
          } else {
            finish(new Error(`[test-utils] the game clock ran only ${Math.round(held)}ms in ${wallGuardMs}ms — ${message} cannot be judged`));
          }
        };
        guard = setTimeout(watch, interval);
      });
    },
    async holdUntil({ press, release, observed, gameBudgetMs = 15_000, target, wallGuardMs = 180_000, interval = 16, message }) {
      requireGameMs('holdUntil: gameBudgetMs', gameBudgetMs); // before the press: a bad budget presses nothing
      resolver.oneProcess(target, 'holdUntil'); // and neither does a target naming more than one clock
      press();
      try {
        await onGameClock(() => observed(), { gameBudgetMs, target, wallGuardMs, interval, message: () => `${failureMessage(message, 'the held input')} to be observed` }, 'holdUntil');
        return { observed: true };
      } finally {
        release();
      }
    },
  };
};
